import {Component, OnDestroy, OnInit} from '@angular/core';
import {CardsBasicComponent} from "../../../../shared/components/cards/cards-basic/cards-basic.component";
import {FormsModule} from "@angular/forms";
import {MatButton} from "@angular/material/button";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {Subscription} from "rxjs";
import {DjangoApiService} from "../../../../shared/services/django-api.service";
import {SnackbarService} from "../../../../shared/services/snackbar.service";
import {LanguageService} from "../../../../shared/services/language.service";
import {LangChangeEvent, TranslateModule, TranslateService} from "@ngx-translate/core";
import {AuthService} from "../../../../shared/services/auth.service";
import {ActivatedRoute, Router} from "@angular/router";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {SlugTransformDirective} from "../../../../shared/directives/slug-transform.directive";

@Component({
  selector: 'app-page-settings',
  standalone: true,
  imports: [
    CardsBasicComponent,
    FormsModule,
    MatButton,
    MatFormField,
    MatInput,
    MatLabel,
    MatCheckboxModule,
    SlugTransformDirective,
    TranslateModule
  ],
  templateUrl: './page-settings.component.html',
  styleUrl: './page-settings.component.css'
})
export class PageSettingsComponent implements OnInit, OnDestroy {
  productId!: number;
  pageId!: number;
  pageData!: any;
  isDefaultPage: boolean = false;
  currentLanguage!: string;
  langChangeSubscription: Subscription | null = null;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private _apiService: DjangoApiService,
    private _snackbarService: SnackbarService,
    public _languageService: LanguageService,
    private _translate: TranslateService,
    private _route: ActivatedRoute,
    private _router: Router) {
  }

  ngOnInit() {
    this.setupLanguageSubscription();
    this.getRouteParams();
  }

  getRouteParams() {
    this.subscriptions.add(
      this._route.params.subscribe(params => {
        this.productId = this._route.snapshot.parent?.params['productId'];
        this.pageId = this._route.snapshot.parent?.params['pageId'];
      })
    );

    if (!isNaN(Number(this.pageId))) {
      this.loadPageData();
    } else {
      this.createEmptyPage();
    }

  }


  createEmptyPage() {
    const translations: { [key: string]: { title: string, slug: string } } = {};

    this._languageService.supportedLangs.forEach(lang => {
      translations[lang] = {
        title: '',
        slug: ''
      };
    });

    this.pageData = {
      product_id: Number(this.productId),
      is_public: false,
      is_downloadable: false,
      translations: translations
    };
  }


  private setupLanguageSubscription(): void {
    this.currentLanguage = this._languageService.getLanguage();
    this.subscriptions.add(
      this.langChangeSubscription = this._translate.onLangChange.subscribe((event: LangChangeEvent) => {
        this.currentLanguage = event.lang;
      })
    );
  }


  private loadPageData() {
    this.subscriptions.add(
      this._apiService.fetchPage(this.pageId).subscribe({
        next: (response: any) => {
          this.pageData = response;
          this.checkIfDefaultPage();
        },
        error: (error: any) => {
          console.error('There was an error fetching the user information', error);
        }
      })
    );
  }

  private checkIfDefaultPage(): void {
    this.isDefaultPage = this.pageData.translations[this.currentLanguage]?.slug === 'overview';
  }

  private createPage() {
    this.subscriptions.add(
      this._apiService.createPage(this.pageData).subscribe({
        next: (response: any) => {
          this.pageData = response.data;
          this._snackbarService.show(response.message);
          this.updateUrl();
        },
        error: (error: any) => {
          console.error('There was an error fetching the user information', error);
        }
      })
    );
  }

  private updatePage() {
    this.subscriptions.add(
      this._apiService.updatePage(this.pageData).subscribe({
        next: (response: any) => {
          this.pageData = response.data;
          this._snackbarService.show(response.message);
        },
        error: (error: any) => {
          console.error('There was an error fetching the user information', error);
        }
      })
    );
  }

  savePage() {
    this.pageData.id ? this.updatePage() : this.createPage();
  }

  cancel() {
    this.pageData.id ? this.loadPageData() : this.createEmptyPage();
  }

  private updateUrl(): void {
    this._router.navigateByUrl(this._router.url.replace(String(this.pageId), String(this.pageData.id)));
    this.pageId = this.pageData.id;
  }

  ngOnDestroy() {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }


}
